import { v4 as uuid } from 'uuid';

import { calculateWeight, sortByWeight } from '@techchange/dnd';
import { SOCIAL_MEDIA_TYPES } from '../../../../../../_shared';

import { CONTINUATION_TYPES, FIELD_TYPES, FORM_TYPES } from '../../../../../constants';
import { findFormFieldIndices } from '../../../../../utils';
import {
	FORM_EDITOR_FIELDS,
	FIELD_EDITOR_FIELDS,
	OPTION_EDITOR_FIELDS,
	SECTION_EDITOR_FIELDS,
} from '../constants';

export const FORM_FIELD_DATA_DISPATCH = {
	ADD_FORM_FIELD: 'ADD_FORM_FIELD',
	CLONE_FORM_FIELD: 'CLONE_FORM_FIELD',
	DELETE_FORM_FIELD: 'DELETE_FORM_FIELD',
	MOVE_FORM_FIELD: 'MOVE_FORM_FIELD',
	UPDATE_FORM_FIELD: 'UPDATE_FORM_FIELD',
	UPDATE_FORM_FIELD_CONTENTS: 'UPDATE_FORM_FIELD_CONTENTS',
};

/**
 * Set of functions that update the form fields state for a given action type.
 */
export const formFieldDataReducers = {
	[FORM_FIELD_DATA_DISPATCH.ADD_FORM_FIELD]: addFormField,
	[FORM_FIELD_DATA_DISPATCH.CLONE_FORM_FIELD]: cloneFormField,
	[FORM_FIELD_DATA_DISPATCH.DELETE_FORM_FIELD]: deleteFormField,
	[FORM_FIELD_DATA_DISPATCH.MOVE_FORM_FIELD]: moveFormField,
	[FORM_FIELD_DATA_DISPATCH.UPDATE_FORM_FIELD]: updateFormField,
	[FORM_FIELD_DATA_DISPATCH.UPDATE_FORM_FIELD_CONTENTS]: updateFormFieldContents,
};

/**
 * Handles adding a new form field to the form.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action            Data related to the requested action.
 * @param {String} action.type       The type of form field to add.
 * @param {String} action.sectionID  The ID of the parent form section, if any.
 * @param {String} action.defaultLanguage  The default language for the form.
 *
 * @returns {Object} result  The updated form data.
 */
export function addFormField(formData, action) {

	const { type, sectionID, defaultLanguage } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	const formType = result.type;

	const sectionIdx =
		result[FORM_EDITOR_FIELDS.SECTIONS]
			.findIndex((section) => section.id === sectionID);

	if (sectionIdx === -1) {
		return result;
	}

	const fields = result[FORM_EDITOR_FIELDS.SECTIONS][sectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	// Find the maximum weight for the existing fields in the section
	const prev = Math.max(
		...fields.map((field) => field[FIELD_EDITOR_FIELDS.WEIGHT]),
		0,
	);

	// Create the new field
	const field = {
		[FIELD_EDITOR_FIELDS.ID]: uuid(),
		[FIELD_EDITOR_FIELDS.TYPE]: type,
		[FIELD_EDITOR_FIELDS.SUBTYPE]: null,
		[FIELD_EDITOR_FIELDS.CONDITIONAL]: false,
		[FIELD_EDITOR_FIELDS.FILTER]: true,
		[FIELD_EDITOR_FIELDS.PRIVACY]: null,
		[FIELD_EDITOR_FIELDS.REQUIRED]: false,
		[FIELD_EDITOR_FIELDS.WEIGHT]: calculateWeight(prev, null),
		[FIELD_EDITOR_FIELDS.FORM_SECTION_ID]: sectionID,
		[FIELD_EDITOR_FIELDS.OPTIONS]: [],
		[FIELD_EDITOR_FIELDS.CONTENTS]: [{
			language: defaultLanguage,
			[FIELD_EDITOR_FIELDS.TITLE]: null,
			[FIELD_EDITOR_FIELDS.PLACEHOLDER]: null,
			[FIELD_EDITOR_FIELDS.HELP]: null,
			[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_CORRECT]: null,
			[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_INCORRECT]: null,
		}],
		[FIELD_EDITOR_FIELDS.DELETED_CONTENTS]: [],
	};

	const defaultOptionProps = {
		[OPTION_EDITOR_FIELDS.CONTINUATION_TYPE]: CONTINUATION_TYPES.DEFAULT,
		[OPTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID]: null,
		[OPTION_EDITOR_FIELDS.FORM_FIELD_ID]: field.id,
		[OPTION_EDITOR_FIELDS.CONTENTS]: [],
		[OPTION_EDITOR_FIELDS.DELETED_CONTENTS]: [],
	};

	if (formType === FORM_TYPES.QUIZ) {
		defaultOptionProps[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER] = false;
	}

	// Check if the new field requires some default options
	if (type === FIELD_TYPES.SELECT ||
		type === FIELD_TYPES.RADIO ||
		type === FIELD_TYPES.CHECKBOXES) {

		field[FIELD_EDITOR_FIELDS.OPTIONS] = [1, 2].map((number) => ({
			...defaultOptionProps,
			[OPTION_EDITOR_FIELDS.ID]: uuid(),
			[OPTION_EDITOR_FIELDS.VALUE]: null,
			[OPTION_EDITOR_FIELDS.WEIGHT]: 10 * number,
			[OPTION_EDITOR_FIELDS.CONTENTS]: [{
				language: defaultLanguage,
				[OPTION_EDITOR_FIELDS.LABEL]: null,
			}],
		}));

	// Check if the new field requires likert value options
	} else if (type === FIELD_TYPES.LIKERT) {

		field[FIELD_EDITOR_FIELDS.OPTIONS] = [1, 2, 3, 4, 5].map((number) => ({
			...defaultOptionProps,
			[OPTION_EDITOR_FIELDS.ID]: uuid(),
			[OPTION_EDITOR_FIELDS.VALUE]: number.toString(),
			[OPTION_EDITOR_FIELDS.WEIGHT]: 10 * number,
			[OPTION_EDITOR_FIELDS.CONTENTS]: [{
				language: defaultLanguage,
				[OPTION_EDITOR_FIELDS.LABEL]: number.toString(),
			}],
		}));

	// Check if the new field requires a sub type
	} else if (type === FIELD_TYPES.SOCIAL_MEDIA) {

		field[FIELD_EDITOR_FIELDS.SUBTYPE] = SOCIAL_MEDIA_TYPES.EMAIL;

	}

	// Add the field to the section
	fields.push(field);

	return result;

}

/**
 * Handles changes to a form field in the form.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action             Data related to the requested action.
 * @param {String} action.fieldID     The ID of the form field to update.
 * @param {String} action.fieldName   The name of the field to update for the form field.
 * @param {String} action.fieldValue  The new value of the field to update for the form field.
 *
 * @returns {Object} result  The updated form data.
 */
export function updateFormField(formData, action) {

	const { fieldID, fieldName, fieldValue } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the field from the nested state
	const [originalSectionIdx, originalFieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionFields =
		result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	if (fieldName === FIELD_EDITOR_FIELDS.OPTIONS) {

		// Update the options for the field data
		originalSectionFields[originalFieldIdx][FIELD_EDITOR_FIELDS.OPTIONS] = fieldValue;

	} else {

		// Update the field data
		originalSectionFields[originalFieldIdx] = {
			...originalSectionFields[originalFieldIdx],
			[fieldName]: fieldValue,
		};

	}

	return result;

}

/**
 * Determines the updated state to reflect the field value for an
 * attribute of a form field content object.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action             Data related to the requested action.
 * @param {String} action.fieldID     The ID of the field to update.
 * @param {String} action.fieldName   The name of the field to update for the field.
 * @param {String} action.fieldValue  The new value of the field to update for the field.
 *
 * @returns {Object}  The updated form state.
 */
function updateFormFieldContents(formData, action) {

	const { fieldID, fieldName, fieldValue } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	const selectedLanguage = formData[FORM_EDITOR_FIELDS.LANGUAGE];

	// Find the field from the nested state
	const [originalSectionIdx, originalFieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionFields =
		result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	const originalFieldData = originalSectionFields[originalFieldIdx];

	// Get the existing multi-language content for the field
	const updatedFieldContents = originalFieldData[FIELD_EDITOR_FIELDS.CONTENTS];

	// Find the translated content for the current language, if any
	const activeTranslationIdx = updatedFieldContents
		.findIndex((content) => content.language === selectedLanguage);

	// Update the multi-language content for the field
	if (activeTranslationIdx >= 0) {

		updatedFieldContents[activeTranslationIdx] = {
			...updatedFieldContents[activeTranslationIdx],
			[fieldName]: fieldValue,
		};

	}

	return result;

}

/**
 * Handles cloning a form field for the form.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action          Data related to the requested action.
 * @param {String} action.fieldID  The ID of the form field to clone.
 *
 * @returns {object} result  The updated form data.
 */
export function cloneFormField(formData, action) {

	const { fieldID } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the field from the nested state
	const [originalSectionIdx, originalFieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionFields =
		result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	const originalFieldData = originalSectionFields[originalFieldIdx];

	let nextItem;

	if ((originalFieldIdx + 1) < originalSectionFields.length) {
		nextItem = originalSectionFields[originalFieldIdx + 1];
	}

	const prevWeight = originalFieldData?.[FIELD_EDITOR_FIELDS.WEIGHT];
	const nextWeight = nextItem?.[FIELD_EDITOR_FIELDS.WEIGHT];

	// Build the new field
	const clonedField = {
		[FIELD_EDITOR_FIELDS.ID]: uuid(),
		[FIELD_EDITOR_FIELDS.TYPE]: originalFieldData.type,
		[FIELD_EDITOR_FIELDS.SUBTYPE]: originalFieldData.subtype,
		[FIELD_EDITOR_FIELDS.CONDITIONAL]: originalFieldData.conditional,
		[FIELD_EDITOR_FIELDS.FILTER]: originalFieldData.filter,
		[FIELD_EDITOR_FIELDS.PRIVACY]: originalFieldData.privacy,
		[FIELD_EDITOR_FIELDS.REQUIRED]: originalFieldData.required,
		[FIELD_EDITOR_FIELDS.WEIGHT]: calculateWeight(prevWeight, nextWeight),
		[FIELD_EDITOR_FIELDS.FORM_SECTION_ID]: originalFieldData.form_section_id,
		[FIELD_EDITOR_FIELDS.OPTIONS]:
			originalFieldData[FIELD_EDITOR_FIELDS.OPTIONS].map((prevOption) => ({
				[OPTION_EDITOR_FIELDS.ID]: uuid(),
				[OPTION_EDITOR_FIELDS.VALUE]: prevOption.value,
				[OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER]: prevOption.is_correct_answer,
				[OPTION_EDITOR_FIELDS.CONTINUATION_TYPE]: prevOption.continuation_type,
				[OPTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID]: prevOption.continuation_section_id,
				[OPTION_EDITOR_FIELDS.WEIGHT]: prevOption[FIELD_EDITOR_FIELDS.WEIGHT],
				[OPTION_EDITOR_FIELDS.CONTENTS]:
					prevOption[OPTION_EDITOR_FIELDS.CONTENTS].map((content) => ({
						language: content.language,
						[OPTION_EDITOR_FIELDS.LABEL]: content.label,
					})),
				[OPTION_EDITOR_FIELDS.DELETED_CONTENTS]: [],
			})),
		[FIELD_EDITOR_FIELDS.CONTENTS]:
			originalFieldData[FIELD_EDITOR_FIELDS.CONTENTS].map((content) => ({
				language: content.language,
				[FIELD_EDITOR_FIELDS.TITLE]: content.title,
				[FIELD_EDITOR_FIELDS.PLACEHOLDER]: content.placeholder,
				[FIELD_EDITOR_FIELDS.HELP]: content.help,
				[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_CORRECT]:
					content.submission_feedback_correct,
				[FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_INCORRECT]:
					content.submission_feedback_incorrect,
			})),
		[FIELD_EDITOR_FIELDS.DELETED_CONTENTS]: [],
	};

	originalSectionFields.push(clonedField);

	result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx][SECTION_EDITOR_FIELDS.FIELDS] =
		sortByWeight(originalSectionFields);

	return result;

}

/**
 * Handles deleting a form field from the form.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action   Data related to the requested action.
 * @param {String} fieldID  The ID of the form field to delete.
 *
 * @returns {Object} result  The updated form data.
 */
export function deleteFormField(formData, action) {

	const { fieldID } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the field from the nested state
	const [originalSectionIdx, originalFieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionFields =
		result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

	// Remove the field from section
	originalSectionFields.splice(originalFieldIdx, 1);

	return result;

}

/**
 * Handles changes to the position of a form field in the form.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action           Data related to the requested action.
 * @param {String} action.fieldID   The id of the form field to move.
 * @param {Object} action.prevItem  The item to place the field after.
 * @param {Object} action.nextItem  The item to place the field before.
 *
 * @returns {Object} result  The updated form data.
 */
export function moveFormField(formData, action) {

	const { fieldID, prevItem, nextItem } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the index of the "moved" field in the list of form fields
	const [originalSectionIdx, originalFieldIdx] = findFormFieldIndices(
		result[FORM_EDITOR_FIELDS.SECTIONS],
		fieldID,
	);

	const originalSectionData = result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx];

	const [prevSectionIdx, prevFieldIdx] = prevItem
		? findFormFieldIndices(result[FORM_EDITOR_FIELDS.SECTIONS], prevItem.id)
		: [null, null];

	const [nextSectionIdx, nextFieldIdx] = nextItem
		? findFormFieldIndices(result[FORM_EDITOR_FIELDS.SECTIONS], nextItem.id)
		: [null, null];

	let prev, next;

	if (prevSectionIdx >= 0 && prevFieldIdx >= 0 && prevItem) {

		const prevFields =
			result[FORM_EDITOR_FIELDS.SECTIONS][prevSectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

		prev = prevFields[prevFieldIdx];

	}

	if (nextSectionIdx >= 0 && nextFieldIdx >= 0 && nextItem) {

		const nextFields =
			result[FORM_EDITOR_FIELDS.SECTIONS][nextSectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

		next = nextFields[nextFieldIdx];

	}

	// Remove the field from the list of form fields within the section
	const [fieldToMove] =
		originalSectionData[SECTION_EDITOR_FIELDS.FIELDS].splice(originalFieldIdx, 1);

	// Check if field is being moved into empty section
	if (!prev && !next) {

		const prevSectionID = prevItem?.[FIELD_EDITOR_FIELDS.FORM_SECTION_ID];
		const nextSectionID = nextItem?.[FIELD_EDITOR_FIELDS.FORM_SECTION_ID];

		const emptySectionID = prevSectionID || nextSectionID;

		// Find the empty section
		const emptySectionIdx = result[FORM_EDITOR_FIELDS.SECTIONS].findIndex(
			(section) => section.id === emptySectionID);

		fieldToMove[FIELD_EDITOR_FIELDS.WEIGHT] = 10;
		fieldToMove[FIELD_EDITOR_FIELDS.FORM_SECTION_ID] = emptySectionID;

		result[FORM_EDITOR_FIELDS.SECTIONS][emptySectionIdx][SECTION_EDITOR_FIELDS.FIELDS]
			.push(fieldToMove);

		return result;

	}

	const prevWeight = prevItem?.[FIELD_EDITOR_FIELDS.WEIGHT];
	const nextWeight = nextItem?.[FIELD_EDITOR_FIELDS.WEIGHT];

	// Calculate the new weight of the "moved" option;
	const weight = calculateWeight(prevWeight, nextWeight);

	fieldToMove[FIELD_EDITOR_FIELDS.WEIGHT] = weight;

	// Use the previous field (if any) to determine where to place the "moved" field
	if (prevItem) {

		// Move the form field, placing it after the previous field

		const prevSectionFields =
			result[FORM_EDITOR_FIELDS.SECTIONS][prevSectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

		fieldToMove[FIELD_EDITOR_FIELDS.FORM_SECTION_ID] =
			prevItem[FIELD_EDITOR_FIELDS.FORM_SECTION_ID];

		prevSectionFields.push(fieldToMove);

		result[FORM_EDITOR_FIELDS.SECTIONS][prevSectionIdx][SECTION_EDITOR_FIELDS.FIELDS] =
			sortByWeight(prevSectionFields);

	// Use the next field (if any) to determine where to place the "moved" field
	} else if (nextItem) {

		// Move the form field, placing it before the next field

		const nextSectionFields =
			result[FORM_EDITOR_FIELDS.SECTIONS][nextSectionIdx][SECTION_EDITOR_FIELDS.FIELDS];

		fieldToMove[FIELD_EDITOR_FIELDS.FORM_SECTION_ID] =
			nextItem[FIELD_EDITOR_FIELDS.FORM_SECTION_ID];

		nextSectionFields.push(fieldToMove);

		result[FORM_EDITOR_FIELDS.SECTIONS][nextSectionIdx][SECTION_EDITOR_FIELDS.FIELDS] =
			sortByWeight(nextSectionFields);

	}

	return result;

}
